// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasOnComplete } from "@insite/client-framework/HandlerCreator";
import { getIsShareEntity } from "@insite/client-framework/ServerSideRendering";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setAddToListModalIsOpen from "@insite/client-framework/Store/Components/AddToListModal/Handlers/SetAddToListModalIsOpen";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { isVmiAdmin, isVmiUser } from "@insite/client-framework/Store/Data/VmiLocations/VmiLocationsSelectors";
import addToWishList, {
    AddToWishListParameter,
} from "@insite/client-framework/Store/Data/WishLists/Handlers/AddToWishList";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, WishListSettingsModel } from "@insite/client-framework/Types/ApiModels";
import CardContainer from "@insite/content-library/Components/CardContainer";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductPartNumbers from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import VmiBinDetailTypeLink from "@insite/content-library/Components/VmiBinDetailTypeLink";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Img from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartLineContainer from "@werner/App/Components/Cart/CartLineContainer";
import { CartLineContainerStyles } from "@werner/App/Components/Cart/CartLineContainer";
import LinesHeader, { LinesHeaderStyles } from "@werner/App/Components/Cart/LinesHeader";
import SectionTitle, { SectionTitleStyles } from "@werner/App/Components/Common/SectionTitle";
import cartPageStyles from "@werner/App/SharedStyle/CartPageStyles";
import orderDetailsSharedStyles from "@werner/App/SharedStyle/OrderDetailsSharedStyles";
import LazyImage, { LazyImageProps } from "@werner/MobiusOverrides/LazyImage/LazyImage";
import { constants } from "@werner/XDL/constants";
import { color, mergeWithCss, parseBoolean, xScreen } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    order?: OrderModel;
    extendedStyles?: OrderLinesListStyles;
}

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const settings = getSettingsCollection(state);
    return {
        wishListSettings: settings.wishListSettings,
        enableVat: settings.productSettings.enableVat,
        vatPriceDisplay: settings.productSettings.vatPriceDisplay,
        vmiBinDetailsPageUrl: getPageLinkByPageType(state, "VmiBinDetailsPage")?.url,
        canViewVmiOrder: isVmiAdmin(settings.orderSettings, session) || isVmiUser(settings.orderSettings, session),
    };
};

const mapDispatchToProps = {
    setAddToListModalIsOpen,
    addToWishList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderLineNotesStyles {
    typographyStyles?: TypographyProps;
    gridItemStyles?: GridItemProps;
}

export interface OrderLinesListStyles {
    gridContainer?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyProps;
    orderLineCardContainer?: GridContainerProps;
    orderLineCardGridItem?: GridItemProps;
    orderLineCardGridContainer?: GridContainerProps;
    orderLineCardImageGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    orderLineCardImage?: LazyImageProps;
    orderLineCardInfoGridItem?: GridItemProps;
    orderLineCardInfoGridContainer?: GridContainerProps;
    orderLineCardAddToListGridItem?: GridItemProps;
    orderLineCardActionsWide?: HiddenProps;
    orderLineCardActionsNarrow?: HiddenProps;
    overflowMenu?: OverflowMenuPresentationProps;
    addToListClickable?: ClickablePresentationProps;
    orderLineCardAddToListButton?: LinkPresentationProps;
    productBrandStyles?: ProductBrandStyles;
    productInfoLink?: LinkPresentationProps;
    productInfoDescription?: TypographyProps;
    productInfoSectionOptionsGridItem?: GridItemProps;
    productInfoSectionOptionsList?: InjectableCss;
    productInfoSectionOptionsListItem?: InjectableCss;
    productInfoPartNumbersGridItem?: GridItemProps;
    productInfoPartNumbersGridContainer?: GridContainerProps;
    productInfoSectionOptionsText?: InjectableCss;
    productInfoGridItem?: GridItemProps;
    productInfoGridContainer?: GridContainerProps;
    productInfoBrandDescriptionGridItem?: GridItemProps;
    productInfoBrandDescriptionGridContainer?: GridContainerProps;
    productInfoBrandGridItem?: GridItemProps;
    productInfoDescriptionGridItem?: GridItemProps;
    productDescriptionStyles?: ProductDescriptionStyles;
    productInfoErpNumberGridItem?: GridItemProps;
    erpNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoManufacturerItemGridItem?: GridItemProps;
    productInfoVmiBinNumberGridItem?: GridItemProps;
    manufacturerHeadingAndText?: SmallHeadingAndTextStyles;
    vmiBinNumberHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoCustomerProductGridItem?: GridItemProps;
    customerProductNumberHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineInfoPromotionGridItem?: GridItemProps;
    orderLineInfoPromotionList?: InjectableCss;
    orderLineInfoPromotionText?: TypographyProps;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoGridContainer?: GridContainerProps;
    orderLinePriceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    vatLabelText?: TypographyPresentationProps;
    secondaryPriceText?: TypographyPresentationProps;
    secondaryVatLabelText?: TypographyPresentationProps;
    productInfoQtyOrderedGridItem?: GridItemProps;
    productInfoQtyOrderedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoQtyShippedGridItem?: GridItemProps;
    productInfoQtyShippedHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalGridItem?: GridItemProps;
    subtotalWithoutVatHeadingAndText?: SmallHeadingAndTextStyles;
    productInfoSubtotalHeadingAndText?: SmallHeadingAndTextStyles;
    orderLineNotes?: OrderLineNotesStyles;
    productInfoTotalsGridItem?: GridItemProps;
    productInfoTotalsGridContainer?: GridContainerProps;
    // XNG-Changes
    sectionTitle?: SectionTitleStyles;
    linesHeaderStyles?: LinesHeaderStyles;
    cartLineContainer?: CartLineContainerStyles;
    shareEntityProductDescriptionStyles?: ProductDescriptionStyles;
}

export const orderLinesListStyles: OrderLinesListStyles = {
    gridContainer: {
        css: css`
            margin-top: 70px;
            @media print {
                page-break-inside: avoid;
            }
        `,
    },
    titleGridItem: {
        width: 12,
    },
    titleText: {
        variant: "h5",
        as: "h2",
        css: css`
            @media print {
                font-size: 15px;
                padding-top: 15px;
                margin-bottom: 5px;
            }
            padding-top: 30px;
            margin-bottom: 0.5rem;
        `,
    },
    orderLineCardContainer: {
        gap: 10,
    },
    orderLineCardGridItem: {
        width: 12,
    },
    orderLineCardImageGridItem: {
        width: 2,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 15px 0 0 0;
            }
        `,
    },
    productImageLink: {
        css: css`
            width: 100px;
            height: 100px;
            & img {
                max-width: 100%;
                max-height: 100%;
                height: auto;
            }
        `,
    },
    orderLineCardInfoGridItem: {
        width: [8, 8, 7, 8, 8],
        printWidth: 10,
        css: css`
            @media print {
                &&&& {
                    max-width: 100%;
                }
            }
        `,
    },
    orderLineCardInfoGridContainer: {
        gap: 10,
    },
    orderLineCardAddToListGridItem: {
        width: [2, 2, 3, 2, 2],
        printWidth: 0,
    },
    orderLineCardActionsWide: {
        above: "sm",
    },
    orderLineCardActionsNarrow: {
        below: "md",
    },
    productInfoSectionOptionsGridItem: {
        width: 12,
    },
    productInfoPartNumbersGridItem: {
        width: 12,
    },
    productInfoGridItem: {
        width: [12, 12, 12, 5, 5],
        printWidth: 6,
    },
    productInfoGridContainer: {
        gap: 5,
    },
    productInfoBrandDescriptionGridItem: {
        width: 12,
    },
    productInfoBrandDescriptionGridContainer: {
        gap: 5,
    },
    productInfoBrandGridItem: {
        width: 12,
    },
    productInfoDescriptionGridItem: {
        width: 12,
    },
    productDescriptionStyles: {
        ...cartPageStyles.cartLine?.productDescription,
    },
    shareEntityProductDescriptionStyles: {
        ...mergeWithCss(cartPageStyles.cartLine?.productDescription, {
            productDetailDescription: {
                size: 18,
                css: css`
                    overflow: hidden;
                    display: block;
                    -webkit-box-orient: unset;
                    -webkit-line-clamp: unset;
                    min-height: 44px;
                    margin-bottom: 8px;
                `,
            },
            productDetailLink: {
                typographyProps: {
                    size: 18,
                    css: css`
                        overflow: hidden;
                        display: block;
                        -webkit-box-orient: unset;
                        -webkit-line-clamp: unset;
                        min-height: 44px;
                        margin-bottom: 8px;
                    `,
                },
                css: css`
                    white-space: pre-wrap;
                `,
            },
        }),
    },
    productInfoErpNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoManufacturerItemGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoVmiBinNumberGridItem: {
        width: [12, 12, 6, 6, 6],
    },
    productInfoCustomerProductGridItem: {
        width: [12, 12, 12, 12, 12],
        printWidth: 12,
    },
    orderLineInfoPromotionGridItem: {
        width: 12,
    },
    orderLineInfoPromotionText: {
        size: 12,
        italic: true,
    },
    orderLineInfoGridItem: {
        width: [12, 12, 12, 7, 7],
        printWidth: 6,
    },
    orderLineInfoGridContainer: {
        gap: 5,
    },
    orderLinePriceGridItem: {
        width: [12, 12, 12, 12, 12],

        css: css`
            flex-direction: column;
        `,
    },
    vatLabelText: {
        size: 12,
    },
    secondaryVatLabelText: {
        size: 12,
    },
    productInfoQtyOrderedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoQtyShippedGridItem: {
        width: [6, 6, 4, 3, 3],
        printWidth: 4,
    },
    productInfoSubtotalGridItem: {
        width: [12, 12, 4, 6, 6],
        printWidth: 4,
    },
    orderLineNotes: {
        gridItemStyles: {
            width: 12,
        },
    },
    productInfoTotalsGridItem: {
        width: [12, 12, 12, 12, 12],
        css: css`
            padding-top: 10px;
        `,
    },
    productInfoTotalsGridContainer: {
        gap: 10,
    },
    productInfoQtyOrderedHeadingAndText: {
        ...mergeWithCss(cartPageStyles.cartLine?.quantity?.readOnlyQuantityText, {
            heading: {
                css: css`
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: block;
                    }
                `,
            },
        }),
    },
    productInfoQtyShippedHeadingAndText: {
        ...mergeWithCss(cartPageStyles.cartLine?.quantity?.readOnlyQuantityText, {
            heading: {
                css: css`
                    ${xScreen("gt", constants.cartLine.breakpoints.md)} {
                        display: block;
                    }
                `,
            },
        }),
    },
    productInfoSubtotalHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    subtotalWithoutVatHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    priceHeadingAndText: {
        ...cartPageStyles.cartLine?.totalSmallHeadingAndText,
    },
    customerProductNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    erpNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    manufacturerHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    vmiBinNumberHeadingAndText: {
        extendedStyles: {
            heading: {
                weight: "bold",
            },
        },
    },
    orderLineCardAddToListButton: {
        ...cartPageStyles.cartLine?.addToListLink,
    },
    cartLineContainer: {
        ...orderDetailsSharedStyles.cartLineContainer,
    },
    sectionTitle: {
        ...orderDetailsSharedStyles.sectionTitle,
    },
    linesHeaderStyles: {
        ...orderDetailsSharedStyles.linesHeaderStyles,
    },
};

const OrderLineProductInfo = ({
    orderLine,
    styles,
    vmiLocationId,
    canViewVmiOrder = false,
}: {
    orderLine: OrderLineModel;
    styles: OrderLinesListStyles;
    vmiLocationId: string;
    canViewVmiOrder?: boolean;
}) => {
    let sectionOptions: JSX.Element[] = [];
    if (orderLine.sectionOptions && orderLine.sectionOptions) {
        sectionOptions = orderLine.sectionOptions.map(option => (
            <li {...styles.productInfoSectionOptionsListItem} key={option.sectionOptionId.toString()}>
                <Typography {...styles.productInfoSectionOptionsText}>
                    {option.sectionName}: {option.optionName}
                </Typography>
            </li>
        ));
    }

    return (
        <GridItem {...styles.productInfoGridItem}>
            <GridContainer {...styles.productInfoGridContainer}>
                <GridItem {...styles.productInfoBrandDescriptionGridItem}>
                    <GridContainer {...styles.productInfoBrandDescriptionGridContainer}>
                        {orderLine.brand && (
                            <GridItem {...styles.productInfoBrandGridItem}>
                                <ProductBrand brand={orderLine.brand} extendedStyles={styles.productBrandStyles} />
                            </GridItem>
                        )}
                        <GridItem {...styles.productInfoDescriptionGridItem}>
                            {canViewVmiOrder && vmiLocationId && orderLine.isActiveVmiBin && (
                                <VmiBinDetailTypeLink
                                    title={orderLine.shortDescription}
                                    locationId={vmiLocationId}
                                    id={orderLine.vmiBinId}
                                />
                            )}
                            {canViewVmiOrder && vmiLocationId && !orderLine.isActiveVmiBin && (
                                <Typography as="p">{orderLine.shortDescription}</Typography>
                            )}
                            {(!canViewVmiOrder || !vmiLocationId) && (
                                <ProductDescription
                                    product={orderLine}
                                    extendedStyles={styles.productDescriptionStyles}
                                />
                            )}
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {sectionOptions.length > 0 && (
                    <GridItem {...styles.productInfoSectionOptionsGridItem}>
                        <ul {...styles.productInfoSectionOptionsList}>{sectionOptions}</ul>
                    </GridItem>
                )}
                <GridItem {...styles.productInfoPartNumbersGridItem}>
                    <GridContainer {...styles.productInfoPartNumbersGridContainer}>
                        {orderLine.customerProductNumber && (
                            <GridItem {...styles.productInfoCustomerProductGridItem}>
                                <SmallHeadingAndText
                                    {...styles.customerProductNumberHeadingAndText}
                                    heading={translate("My Part #")}
                                    text={orderLine.customerProductNumber}
                                />
                            </GridItem>
                        )}
                        <GridItem {...styles.productInfoErpNumberGridItem}>
                            <SmallHeadingAndText
                                {...styles.erpNumberHeadingAndText}
                                heading={translate("Item #")}
                                text={orderLine.productErpNumber}
                            />
                        </GridItem>
                        {orderLine.manufacturerItem && (
                            <GridItem {...styles.productInfoManufacturerItemGridItem}>
                                <SmallHeadingAndText
                                    {...styles.manufacturerHeadingAndText}
                                    heading={translate("MFG #")}
                                    text={orderLine.manufacturerItem}
                                />
                            </GridItem>
                        )}
                        {orderLine.vmiBinNumber && (
                            <GridItem {...styles.productInfoVmiBinNumberGridItem}>
                                <SmallHeadingAndText
                                    {...styles.vmiBinNumberHeadingAndText}
                                    heading={translate("Bin #")}
                                    text={orderLine.vmiBinNumber}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
            </GridContainer>
        </GridItem>
    );
};

const mapStateToOrderLineInfoProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const OrderLineInfoProto = ({
    orderLine,
    order,
    enableVat,
    vatPriceDisplay,
    styles,
    showPricing,
}: {
    orderLine: OrderLineModel;
    order: OrderModel;
    enableVat: boolean;
    vatPriceDisplay: string;
    styles: OrderLinesListStyles;
    showPricing?: boolean;
}) => {
    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }

    return (
        <GridItem {...styles.orderLineInfoGridItem}>
            <GridContainer {...styles.orderLineInfoGridContainer}>
                {showPricing && (
                    <GridItem {...styles.orderLinePriceGridItem}>
                        <SmallHeadingAndText
                            {...styles.priceHeadingAndText}
                            heading={translate("Price")}
                            text={
                                (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? orderLine.unitPriceWithVatDisplay
                                    : orderLine.unitPriceDisplay) +
                                (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                            }
                        />
                        {enableVat && (
                            <>
                                <Typography as="p" {...styles.vatLabelText}>
                                    {vatPriceDisplay === "DisplayWithVat" ||
                                    vatPriceDisplay === "DisplayWithAndWithoutVat"
                                        ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                        : translate("Ex. VAT")}
                                </Typography>
                                {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <Typography {...styles.secondaryPriceText} as="p">
                                            {orderLine.unitPriceDisplay +
                                                (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")}
                                        </Typography>
                                        <Typography as="p" {...styles.secondaryVatLabelText}>
                                            {translate("Ex. VAT")}
                                        </Typography>
                                    </>
                                )}
                            </>
                        )}
                    </GridItem>
                )}
                <GridItem {...styles.productInfoTotalsGridItem}>
                    <GridContainer {...styles.productInfoTotalsGridContainer}>
                        <GridItem {...styles.productInfoQtyOrderedGridItem}>
                            <SmallHeadingAndText
                                {...styles.productInfoQtyOrderedHeadingAndText}
                                heading={translate("QTY Ordered")}
                                data-test-selector="qty_Ordered"
                                text={`${orderLine.qtyOrdered}`}
                            />
                        </GridItem>
                        <GridItem {...styles.productInfoQtyShippedGridItem}>
                            <SmallHeadingAndText
                                {...styles.productInfoQtyShippedHeadingAndText}
                                heading={translate("QTY Shipped")}
                                text={`${orderLine.qtyShipped}`}
                            />
                        </GridItem>
                        {showPricing && (
                            <GridItem {...styles.productInfoSubtotalGridItem}>
                                {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <SmallHeadingAndText
                                        heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                        text={orderLine.extendedUnitNetPriceDisplay}
                                        extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                    />
                                )}
                                <SmallHeadingAndText
                                    heading={
                                        !enableVat
                                            ? translate("Subtotal")
                                            : vatPriceDisplay !== "DisplayWithoutVat"
                                              ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                              : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                    }
                                    text={
                                        enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                            ? orderLine.netPriceWithVatDisplay
                                            : orderLine.extendedUnitNetPriceDisplay
                                    }
                                    extendedStyles={styles.productInfoSubtotalHeadingAndText}
                                />
                            </GridItem>
                        )}
                    </GridContainer>
                </GridItem>
                {promotions.length > 0 && (
                    <GridItem {...styles.orderLineInfoPromotionGridItem}>
                        <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                    </GridItem>
                )}
            </GridContainer>
        </GridItem>
    );
};

const OrderLineInfo = connect(mapStateToOrderLineInfoProps)(OrderLineInfoProto);

const OrderLineNotes = ({
    orderLine,
    extendedStyles,
}: {
    orderLine: OrderLineModel;
    extendedStyles?: OrderLineNotesStyles;
}) => {
    return orderLine.notes ? (
        <GridItem {...extendedStyles?.gridItemStyles}>
            <Typography {...extendedStyles?.typographyStyles}>{orderLine.notes}</Typography>
        </GridItem>
    ) : null;
};

const OrderLineCard = (props: {
    orderLine: OrderLineModel;
    order: OrderModel;
    wishListSettings?: WishListSettingsModel;
    vmiBinDetailsPageUrl?: string;
    styles: OrderLinesListStyles;
    canViewVmiOrder?: boolean;
    setAddToListModalIsOpen: (parameter: {
        productInfos?: Omit<ProductInfo, "productDetailPath">[];
        modalIsOpen: boolean;
    }) => void;
    addToWishList: (parameter: AddToWishListParameter & HasOnComplete) => void;
}) => {
    const {
        orderLine,
        order,
        wishListSettings,
        vmiBinDetailsPageUrl,
        styles,
        setAddToListModalIsOpen,
        addToWishList,
        canViewVmiOrder = false,
    } = props;
    const toasterContext = React.useContext(ToasterContext);
    // <XNG-Changes>
    const { enableVat, vatPriceDisplay } = useAppSelector(state => ({
        enableVat: getSettingsCollection(state).productSettings.enableVat,
        vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    }));
    let promotions: JSX.Element[] = [];
    if (order.orderPromotions) {
        promotions = order.orderPromotions
            .filter(promotion => promotion.orderHistoryLineId === orderLine.id)
            .map(promotion => (
                <Typography {...styles.orderLineInfoPromotionText} as="li" key={promotion.id}>
                    {promotion.name}
                </Typography>
            ));
    }
    // </XNG-Changes>
    const addToListClickHandler = () => {
        if (!wishListSettings) {
            return;
        }

        const productInfo = {
            productId: orderLine.productId,
            qtyOrdered: orderLine.qtyOrdered,
            unitOfMeasure: orderLine.unitOfMeasure,
        };
        if (!wishListSettings.allowMultipleWishLists) {
            addToWishList({
                productInfos: [productInfo],
                onSuccess: () => {
                    toasterContext.addToast({ body: siteMessage("Lists_ProductAdded"), messageType: "success" });
                },
            });
            return;
        }

        setAddToListModalIsOpen({ modalIsOpen: true, productInfos: [productInfo] });
    };

    const isShareEntity = getIsShareEntity();

    const hasVmi = orderLine.isActiveProduct && !!order.vmiLocationId && orderLine.isActiveVmiBin;
    const hasProduct = orderLine.isActiveProduct && !!orderLine.productUri;
    const showProductLink = (!canViewVmiOrder && hasProduct) || (canViewVmiOrder && !hasVmi && hasProduct);
    const showVmiLink = canViewVmiOrder && hasVmi;
    const showNoLink = !showProductLink && !showVmiLink;

    // <XNG-Changes>

    if (isShareEntity) {
        return (
            <GridContainer
                gap={20}
                css={css`
                    padding: 25px 0;
                    border-bottom: 1px solid ${color("common.border")};
                    @media print {
                        page-break-inside: avoid;
                        display: block;
                    }
                `}
            >
                <GridItem width={2}>
                    {orderLine.productUri && orderLine.isActiveProduct && !order.vmiLocationId && (
                        <Link
                            {...styles.productImageLink}
                            href={orderLine.productUri}
                            aria-label={`OrderLine Product Link #${order.erpOrderNumber}`}
                        >
                            <Img src={orderLine.mediumImagePath} altText={order.erpOrderNumber} />
                        </Link>
                    )}
                </GridItem>
                <GridItem width={10}>
                    <GridContainer gap={18}>
                        <GridItem width={[12, 12, 5, 5, 5]} className="left-side">
                            <GridContainer gap={0}>
                                <GridItem width={10}>
                                    <GridContainer gap={20}>
                                        <GridItem width={12}>
                                            <ProductDescription
                                                product={orderLine}
                                                extendedStyles={styles.shareEntityProductDescriptionStyles}
                                            />
                                        </GridItem>
                                        <GridItem width={12}>
                                            <ProductPartNumbers
                                                productNumber={
                                                    orderLine.properties.nonStockProductErpNumber ||
                                                    orderLine.productErpNumber
                                                }
                                                manufacturerItem={orderLine.manufacturerItem}
                                                customerProductNumber={orderLine.customerName}
                                                {...{ isShareEntity: true }}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                        <GridItem width={[12, 12, 7, 7, 7]} className="right-side">
                            <GridContainer gap={8}>
                                <GridItem width={4}>
                                    <GridContainer gap={0}>
                                        <GridItem width={12}>
                                            <SmallHeadingAndText
                                                extendedStyles={styles.productInfoQtyOrderedHeadingAndText}
                                                heading={translate("QTY Ordered")}
                                                data-test-selector="qty_Ordered"
                                                text={`${orderLine.qtyOrdered}`}
                                            />
                                        </GridItem>
                                        <GridItem width={12}>
                                            <SmallHeadingAndText
                                                extendedStyles={styles.productInfoQtyShippedHeadingAndText}
                                                heading={translate("QTY Shipped")}
                                                text={`${orderLine.qtyShipped}`}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </GridItem>
                                <GridItem
                                    width={4}
                                    data-test-selector="price"
                                    css={css`
                                        align-items: flex-start;
                                    `}
                                >
                                    <SmallHeadingAndText
                                        extendedStyles={styles.priceHeadingAndText}
                                        heading={translate("Price")}
                                        text={
                                            parseBoolean(orderLine.properties.isNonStockItem)
                                                ? translate("Requires Quote")
                                                : (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                      ? orderLine.unitPriceWithVatDisplay
                                                      : orderLine.unitPriceDisplay) +
                                                  (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                                        }
                                    />
                                    {enableVat && (
                                        <>
                                            <Typography as="p" {...styles.vatLabelText}>
                                                {vatPriceDisplay === "DisplayWithVat" ||
                                                vatPriceDisplay === "DisplayWithAndWithoutVat"
                                                    ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                                    : translate("Ex. VAT")}
                                            </Typography>
                                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                                <>
                                                    <Typography {...styles.secondaryPriceText} as="p">
                                                        {orderLine.unitPriceDisplay +
                                                            (orderLine.unitOfMeasure
                                                                ? ` / ${orderLine.unitOfMeasure}`
                                                                : "")}
                                                    </Typography>
                                                    <Typography as="p" {...styles.secondaryVatLabelText}>
                                                        {translate("Ex. VAT")}
                                                    </Typography>
                                                </>
                                            )}
                                        </>
                                    )}
                                </GridItem>
                                <GridItem width={4} data-test-selector="subtotal">
                                    {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                        <SmallHeadingAndText
                                            heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                            text={orderLine.extendedUnitNetPriceDisplay}
                                            extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                                        />
                                    )}
                                    <SmallHeadingAndText
                                        heading={
                                            !enableVat
                                                ? translate("Subtotal")
                                                : vatPriceDisplay !== "DisplayWithoutVat"
                                                  ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                                  : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                        }
                                        text={
                                            enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                                ? orderLine.netPriceWithVatDisplay
                                                : orderLine.extendedUnitNetPriceDisplay
                                        }
                                        extendedStyles={styles.productInfoSubtotalHeadingAndText}
                                    />
                                </GridItem>
                            </GridContainer>
                        </GridItem>
                    </GridContainer>
                </GridItem>
                {orderLine.notes && (
                    <GridItem width={12} className="notes-field-section">
                        <OrderLineNotes extendedStyles={styles.orderLineNotes} orderLine={orderLine} />
                    </GridItem>
                )}
            </GridContainer>
        );
    }
    // </XNG-Changes>
    return (
        <CartLineContainer
            data-test-selector={`orderLineCard${orderLine.productId}`}
            extendedStyles={styles.cartLineContainer}
        >
            {/* XNG-Changes */}
            <CartLineContainer.Image>
                {showProductLink ? (
                    <Link
                        {...styles.productImageLink}
                        href={
                            orderLine.properties && parseBoolean(orderLine.properties.isEventOrderLine)
                                ? orderLine.properties.eventUrl
                                : orderLine.productUri
                        }
                        aria-label={`${orderLine.shortDescription}`}
                    >
                        <LazyImage
                            {...styles.orderLineCardImage}
                            src={orderLine.mediumImagePath}
                            isShareEntity={isShareEntity}
                            altText={`${orderLine.shortDescription}`}
                            customTransformation="small"
                            {...{ disableLazyLoad: true }}
                        />
                    </Link>
                ) : (
                    <LazyImage
                        {...styles.orderLineCardImage}
                        src={orderLine.mediumImagePath}
                        isShareEntity={isShareEntity}
                        altText={`${orderLine.shortDescription}`}
                        customTransformation="small"
                        {...{ disableLazyLoad: true }}
                    />
                )}
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <ProductDescription product={orderLine} extendedStyles={styles.productDescriptionStyles} />
            </CartLineContainer.Description>
            <CartLineContainer.PartNumbers>
                <ProductPartNumbers
                    productNumber={orderLine.properties.nonStockProductErpNumber || orderLine.productErpNumber}
                    showManufacturerItem={!parseBoolean(orderLine.properties.isNonStockItem)}
                    showCustomerName={!parseBoolean(orderLine.properties.isNonStockItem)}
                    manufacturerItem={orderLine.manufacturerItem}
                    customerProductNumber={orderLine.customerName}
                />
            </CartLineContainer.PartNumbers>
            <CartLineContainer.Description>
                <OrderLineProductInfo
                    orderLine={orderLine}
                    styles={styles}
                    vmiLocationId={order.vmiLocationId}
                    canViewVmiOrder={canViewVmiOrder}
                />
                <OrderLineNotes orderLine={orderLine} />
            </CartLineContainer.Description>
            <CartLineContainer.Qty>
                <StyledWrapper className="qty-shipped-ordered">
                    <SmallHeadingAndText
                        extendedStyles={styles.productInfoQtyOrderedHeadingAndText}
                        heading={translate("QTY Ordered")}
                        data-test-selector="qty_Ordered"
                        text={`${orderLine.qtyOrdered}`}
                    />
                    <SmallHeadingAndText
                        extendedStyles={styles.productInfoQtyShippedHeadingAndText}
                        heading={translate("QTY Shipped")}
                        text={`${orderLine.qtyShipped}`}
                    />
                </StyledWrapper>
            </CartLineContainer.Qty>
            <CartLineContainer.Price>
                <>
                    <SmallHeadingAndText
                        extendedStyles={styles.priceHeadingAndText}
                        heading={translate("Price")}
                        text={
                            parseBoolean(orderLine.properties.isNonStockItem)
                                ? translate("Requires Quote")
                                : (enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                      ? orderLine.unitPriceWithVatDisplay
                                      : orderLine.unitPriceDisplay,
                                  orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")
                        }
                    />
                    {enableVat && (
                        <>
                            <Typography as="p" {...styles.vatLabelText}>
                                {vatPriceDisplay === "DisplayWithVat" || vatPriceDisplay === "DisplayWithAndWithoutVat"
                                    ? `${translate("Inc. VAT")} (${orderLine.taxRate}%)`
                                    : translate("Ex. VAT")}
                            </Typography>
                            {vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                <>
                                    <Typography {...styles.secondaryPriceText} as="p">
                                        {orderLine.unitPriceDisplay +
                                            (orderLine.unitOfMeasure ? ` / ${orderLine.unitOfMeasure}` : "")}
                                    </Typography>
                                    <Typography as="p" {...styles.secondaryVatLabelText}>
                                        {translate("Ex. VAT")}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </>
            </CartLineContainer.Price>
            <CartLineContainer.Subtotal>
                <>
                    {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                        <SmallHeadingAndText
                            heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                            text={orderLine.extendedUnitNetPriceDisplay}
                            extendedStyles={styles.subtotalWithoutVatHeadingAndText}
                        />
                    )}
                    <SmallHeadingAndText
                        heading={
                            !enableVat
                                ? translate("Subtotal")
                                : vatPriceDisplay !== "DisplayWithoutVat"
                                  ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                  : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                        }
                        text={
                            enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                ? orderLine.netPriceWithVatDisplay
                                : orderLine.extendedUnitNetPriceDisplay
                        }
                        extendedStyles={styles.productInfoSubtotalHeadingAndText}
                    />
                </>
            </CartLineContainer.Subtotal>
            <CartLineContainer.LineNotes>
                <OrderLineNotes extendedStyles={styles.orderLineNotes} orderLine={orderLine} />
            </CartLineContainer.LineNotes>
            {promotions.length > 0 && (
                <CartLineContainer.Errors>
                    <GridItem {...styles.orderLineInfoPromotionGridItem}>
                        <ul {...styles.orderLineInfoPromotionList}>{promotions}</ul>
                    </GridItem>
                </CartLineContainer.Errors>
            )}
            <CartLineContainer.AddToList>
                {orderLine.canAddToWishlist && (
                    <Link
                        {...styles.orderLineCardAddToListButton}
                        onClick={addToListClickHandler}
                        aria-label="add to list"
                    >
                        {translate("Product List")}
                    </Link>
                )}
            </CartLineContainer.AddToList>
        </CartLineContainer>
        // <GridContainer
        //     {...styles.orderLineCardGridContainer}
        //     data-test-selector={`orderLineCard${orderLine.productId}`}
        // >
        //     <GridItem {...styles.orderLineCardImageGridItem}>
        //         {showProductLink  && (
        //             <Link {...styles.productImageLink} href={orderLine.productUri}>
        //                 <LazyImage
        //                     {...styles.orderLineCardImage}
        //                     src={orderLine.mediumImagePath}
        //                     isShareEntity={isShareEntity}
        //                 />
        //             </Link>
        //         )}
        // {showVmiLink && (
        //     <Link
        //         {...styles.productImageLink}
        //         href={`${vmiBinDetailsPageUrl}?locationId=${order.vmiLocationId}&id=${orderLine.vmiBinId}`}
        //     >
        //         <LazyImage
        //             {...styles.orderLineCardImage}
        //             src={orderLine.mediumImagePath}
        //             isShareEntity={isShareEntity}
        //         />
        //     </Link>
        // )}
        // {showNoLink  && (
        //     <LazyImage
        //         {...styles.orderLineCardImage}
        //         src={orderLine.mediumImagePath}
        //         isShareEntity={isShareEntity}
        //     />
        // )}
        //     </GridItem>
        //     <GridItem {...styles.orderLineCardInfoGridItem}>
        //         <GridContainer {...styles.orderLineCardInfoGridContainer}>
        //             <OrderLineProductInfo orderLine={orderLine} styles={styles} />
        //             <OrderLineInfo orderLine={orderLine} order={order} styles={styles} />
        //             <OrderLineNotes extendedStyles={styles.orderLineNotes} orderLine={orderLine} />
        //         </GridContainer>
        //     </GridItem>
        //     <GridItem {...styles.orderLineCardAddToListGridItem}>
        //         <Hidden {...styles.orderLineCardActionsWide}>
        //             {orderLine.canAddToWishlist && (
        //                 <OverflowMenu position="end" {...styles.overflowMenu}>
        //                     <Clickable {...styles.addToListClickable} onClick={addToListClickHandler}>
        //                         {translate("Add to List")}
        //                     </Clickable>
        //                 </OverflowMenu>
        //             )}
        //         </Hidden>
        //         <Hidden {...styles.orderLineCardActionsNarrow}>
        //             {orderLine.canAddToWishlist && (
        //                 <Button {...styles.orderLineCardAddToListButton} onClick={addToListClickHandler}>
        //                     {translate("Add to List")}
        //                 </Button>
        //             )}
        //         </Hidden>
        //     </GridItem>
        // </GridContainer>
    );
};

const OrderLinesList: FC<Props> = ({
    order,
    wishListSettings,
    vmiBinDetailsPageUrl,
    setAddToListModalIsOpen,
    addToWishList,
    extendedStyles,
    canViewVmiOrder,
}) => {
    const [styles] = React.useState(() => mergeToNew(orderLinesListStyles, extendedStyles));
    // <XNG-Changes>
    const isShareEntity = getIsShareEntity();
    // </XNG-Changes>

    if (!order || !order.orderLines) {
        return null;
    }

    return (
        <StyledWrapper {...styles.gridContainer} className={!isShareEntity ? "print-view" : ""}>
            <SectionTitle
                title={translate("Products")}
                extendedStyles={styles.sectionTitle}
                className={isShareEntity ? "share-entity-section-title" : ""}
            />
            <LinesHeader leftTitle={translate("Product")} extendedStyles={styles.linesHeaderStyles} />
            {order.orderLines.map((orderLine, index) => (
                <OrderLineCard
                    orderLine={orderLine}
                    order={order}
                    wishListSettings={wishListSettings}
                    vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
                    styles={styles}
                    canViewVmiOrder={canViewVmiOrder}
                    setAddToListModalIsOpen={setAddToListModalIsOpen}
                    addToWishList={addToWishList}
                    key={orderLine.id}
                />
            ))}
        </StyledWrapper>
        // <GridContainer {...styles.gridContainer}>
        //     <GridItem {...styles.titleGridItem}>
        //         <Typography {...styles.titleText}>{translate("Order Summary")}</Typography>
        //     </GridItem>
        //     {order.orderLines.map(orderLine => (
        //         <CardContainer {...styles.orderLineCardContainer} key={orderLine.id}>
        //             <GridItem {...styles.orderLineCardGridItem}>
        //                 <OrderLineCard
        //                     orderLine={orderLine}
        //                     order={order}
        //                     wishListSettings={wishListSettings}
        //                     vmiBinDetailsPageUrl={vmiBinDetailsPageUrl}
        //                     styles={styles}
        //                     canViewVmiOrder={canViewVmiOrder}
        //                     setAddToListModalIsOpen={setAddToListModalIsOpen}
        //                     addToWishList={addToWishList}
        //                 />
        //             </GridItem>
        //         </CardContainer>
        //     ))}
        // </GridContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderLinesList);
